import { Alert, Card, Col, DatePicker, Form, Row, Select, Spin } from "antd";
import { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import {
  FiscalPeriod,
  MAX_PAGE_SIZE,
  PaginationInput,
} from "../../repository/data.default";
import {
  AnalyticsRange,
  bucketByDay,
  bucketByFiscalPeriods,
  buildCompanyMetricQuery,
  groupByField,
  resolveRange,
} from "./AnalyticsRange";
import { ChartKind, renderMetricChart } from "./MetricChartRender";

// A metric is declared once; the chart owns the range selector + fetch + aggregation + render. Window modes use the
// metric's primary display (trend line / breakdown pie|ring|column); the "Compare by period" mode buckets the metric
// across the company's fiscal periods (column) — the fiscal-period showcase. Future metrics = one more descriptor.
export interface MetricDescriptor {
  readonly key: string;
  readonly title: string;
  readonly fetch: (
    input: PaginationInput,
  ) => Promise<{ data: any[]; totalRecords: number }>;
  readonly dateField: string;
  readonly display:
    | { kind: "trend"; valueLabel?: string }
    | { kind: "breakdown"; groupField: string; chart: "pie" | "ring" | "column"; valueLabel?: string };
}

export interface MetricChartProps {
  readonly companyUid: string;
  readonly fiscalPeriods: FiscalPeriod[];
  readonly metric: MetricDescriptor;
}

function MetricChart(props: MetricChartProps) {
  const { metric, companyUid, fiscalPeriods } = props;

  const [selected, setSelected] = useState<string>("preset:60d");
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [trendData, setTrendData] = useState<{ [day: string]: number }>({});
  const [groupData, setGroupData] = useState<{ type: string; value: number }[]>([]);
  const [chartKind, setChartKind] = useState<ChartKind>(
    metric.display.kind === "trend" ? "line" : metric.display.chart === "ring" ? "ring" : metric.display.chart,
  );

  // Range options: window presets, each fiscal period (as a window), and per-type comparisons.
  const options = useMemo(() => {
    const fiscalOptions = [...fiscalPeriods]
      .sort((a, b) => a.startDate.localeCompare(b.startDate))
      .map((p) => ({ label: p.name, value: `fiscal:${p.uid}` }));

    const compareKeys = new Set<string>();
    fiscalPeriods.forEach((p) => {
      if (p.periodType === "Quarter" || p.periodType === "Semester")
        compareKeys.add(`${p.periodType}:${p.year}`);
    });
    const compareOptions = Array.from(compareKeys).sort().map((k) => {
      const [type, year] = k.split(":");
      return { label: `By ${type} ${year}`, value: `compare:${k}` };
    });

    return [
      {
        label: "Window",
        options: [
          { label: "All time", value: "all" },
          { label: "Last 30 days", value: "preset:30d" },
          { label: "Last 60 days", value: "preset:60d" },
          { label: "This quarter", value: "preset:quarter" },
          { label: "This semester", value: "preset:semester" },
          { label: "This year", value: "preset:year" },
          { label: "Custom range…", value: "custom" },
        ],
      },
      ...(fiscalOptions.length ? [{ label: "Fiscal period", options: fiscalOptions }] : []),
      ...(compareOptions.length ? [{ label: "Compare by period", options: compareOptions }] : []),
    ];
  }, [fiscalPeriods]);

  useEffect(() => {
    if (!companyUid) return;
    let cancelled = false;
    setLoading(true);
    setError(false);

    const run = async () => {
      // Comparison mode: bucket the metric across the company's periods of a type+year.
      if (selected.startsWith("compare:")) {
        const [, type, yearStr] = selected.split(":");
        const periods = fiscalPeriods.filter(
          (p) => p.periodType === (type as any) && `${p.year}` === yearStr,
        );
        if (!periods.length) {
          setGroupData([]);
          setChartKind("column");
          setLoading(false);
          return;
        }
        const start = periods.reduce((m, p) => (p.startDate < m ? p.startDate : m), periods[0].startDate);
        const end = periods.reduce((m, p) => (p.endDate > m ? p.endDate : m), periods[0].endDate);
        const resolved = resolveRange(
          { mode: "custom", custom: { start, end } },
          fiscalPeriods,
        );
        const res = await metric.fetch({
          searchQuery: buildCompanyMetricQuery(companyUid, metric.dateField, resolved),
          pageNumber: 0,
          pageSize: MAX_PAGE_SIZE,
        });
        if (cancelled) return;
        setGroupData(bucketByFiscalPeriods(res.data, metric.dateField, periods));
        setChartKind("column");
        setLoading(false);
        return;
      }

      // Custom range: wait until both ends are picked before fetching.
      if (selected === "custom" && !(customRange && customRange[0] && customRange[1])) {
        setTrendData({});
        setGroupData([]);
        setLoading(false);
        return;
      }

      // Window mode: build the range, fetch, then apply the metric's primary display.
      const range: AnalyticsRange = selected.startsWith("fiscal:")
        ? { mode: "fiscal", fiscalPeriodUid: selected.slice("fiscal:".length) }
        : selected.startsWith("preset:")
          ? { mode: "preset", preset: selected.slice("preset:".length) as any }
          : selected === "custom"
            ? {
                mode: "custom",
                custom: {
                  start: customRange![0].format("YYYY-MM-DD"),
                  end: customRange![1].format("YYYY-MM-DD"),
                },
              }
            : { mode: "all" };
      const resolved = resolveRange(range, fiscalPeriods);
      const res = await metric.fetch({
        searchQuery: buildCompanyMetricQuery(companyUid, metric.dateField, resolved),
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      });
      if (cancelled) return;
      if (metric.display.kind === "trend") {
        setTrendData(bucketByDay(res.data, metric.dateField));
        setChartKind("line");
      } else {
        setGroupData(groupByField(res.data, metric.display.groupField));
        setChartKind(metric.display.chart === "ring" ? "ring" : metric.display.chart);
      }
      setLoading(false);
    };

    run().catch(() => {
      if (!cancelled) {
        setError(true);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [selected, customRange, companyUid, fiscalPeriods, metric]);

  const valueLabel =
    (metric.display.kind === "trend" ? metric.display.valueLabel : metric.display.valueLabel) ?? "Count";

  return (
    <Card>
      <Row className="flex flex-col gap-2">
        <Col className="flex flex-row items-center justify-center gap-2 self-center">
          <Form.Item className="m-0" label="Range">
            <Select
              className="w-56"
              value={selected}
              options={options}
              onChange={setSelected}
              loading={loading}
            />
          </Form.Item>
          {selected === "custom" && (
            <DatePicker.RangePicker
              value={customRange as any}
              onChange={(v) => setCustomRange(v as [Dayjs, Dayjs] | null)}
              allowClear
            />
          )}
        </Col>

        {error && (
          <Alert className="self-center" message="Something went wrong, try again" type="error" />
        )}

        <Col className="flex flex-col">
          <Spin spinning={loading}>{renderMetricChart(chartKind, trendData, groupData, valueLabel)}</Spin>
        </Col>
      </Row>
    </Card>
  );
}

export default MetricChart;
